/**
 * Fetch editorial engagements for the signed-in desktop user (editor role).
 */

import { cloudAuthDesktopV2 } from "./cloudAuthDesktopV2";
import { resolveAutoSyncMode } from "./desktopV2AutoSync";

export type EditorEngagementGrant = {
  bookId: string;
  title: string;
  versionId: string | null;
};

export type EditorEngagement = {
  id: string;
  status: string;
  client: { id: string; name: string | null; email: string };
  grantedBooks: EditorEngagementGrant[];
};

export async function listEditorEngagements(): Promise<EditorEngagement[]> {
  if (resolveAutoSyncMode() !== "web" || !cloudAuthDesktopV2.isSignedIn()) return [];
  try {
    return await cloudAuthDesktopV2.listEditorialEngagements("editor");
  } catch {
    return [];
  }
}

/** Find client display name for a granted book (first matching engagement). */
export function clientNameForBook(engagements: EditorEngagement[], bookId: string): string | null {
  for (const eng of engagements) {
    if (eng.status !== "approved") continue;
    if (eng.grantedBooks.some((g) => g.bookId === bookId)) {
      return (eng.client.name || "").trim() || eng.client.email || "Client";
    }
  }
  return null;
}

export function versionFolderNameForGrant(
  engagements: EditorEngagement[],
  bookId: string,
  bookMetadata?: Record<string, unknown> | null
): string {
  for (const eng of engagements) {
    const grant = eng.grantedBooks.find((g) => g.bookId === bookId);
    if (grant?.versionId) {
      const versions = Array.isArray(bookMetadata?.cloudVersions)
        ? (bookMetadata!.cloudVersions as Array<{ id?: string; name?: string }>)
        : [];
      const match = versions.find((v) => v.id === grant.versionId);
      if (match?.name?.trim()) return match.name.trim();
      return `Version-${grant.versionId.slice(0, 8)}`;
    }
  }
  return "Current";
}
